/**
 * SOAR Collection Main
 * Custom element for the SOAR collection page.
 * - Sort dropdown: writes sort_by to URL and reloads
 * - Quick-add: POSTs to /cart/add.js, dispatches `cart:add` event
 * - Hover image swap is CSS-only (see soar-collection-main.css)
 */
(function () {
  if (window.customElements.get('soar-collection-main')) return;

  class SoarCollectionMain extends HTMLElement {
    connectedCallback() {
      this.bindSort();
      this.bindQuickAdd();
    }

    /* --- Sort dropdown (custom) --- */
    bindSort() {
      const host = this;
      const root = this.querySelector('[data-soar-sort-root]');
      if (!root) return;
      const button = root.querySelector('[data-soar-sort-button]');
      const menu = root.querySelector('[data-soar-sort-menu]');
      const current = root.querySelector('[data-soar-sort-current]');
      if (!button || !menu) return;

      const close = () => {
        root.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
      };
      const open = () => {
        root.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
      };

      button.addEventListener('click', (e) => {
        e.stopPropagation();
        if (root.classList.contains('is-open')) close();
        else open();
      });

      // Navigate with the new sort. Adding #grid makes the reloaded page land
      // on the product grid instead of scrolling back to the very top.
      const applySort = (value) => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', value);
        // Remove page param so sort resets to page 1
        url.searchParams.delete('page');
        url.hash = 'grid';
        close();
        window.location.assign(url.toString());
      };

      menu.querySelectorAll('[data-soar-sort-option]').forEach((opt) => {
        const choose = () => applySort(opt.dataset.value);
        opt.addEventListener('click', choose);
        opt.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            choose();
          }
        });
      });

      // Close on outside click / Escape.
      document.addEventListener('click', (e) => {
        if (!root.contains(e.target)) close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
      });
    }

    /* --- Quick add --- */
    bindQuickAdd() {
      const buttons = this.querySelectorAll('button[data-soar-quick-add]');
      buttons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleQuickAdd(btn);
        });
      });
    }

    async handleQuickAdd(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const originalText = button.querySelector('.soar-collection-main__quick-add-text');
      const originalLabel = originalText ? originalText.textContent : 'Quick add';

      button.disabled = true;
      if (originalText) originalText.textContent = 'Adding…';

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Could not add to cart');
        }

        if (originalText) originalText.textContent = 'Added';
        document.dispatchEvent(new CustomEvent('cart:add'));

        setTimeout(() => {
          if (originalText) originalText.textContent = originalLabel;
          button.disabled = false;
        }, 1200);
      } catch (err) {
        console.error('[SoarCollectionMain] Quick add failed:', err);
        if (originalText) originalText.textContent = 'Try again';
        setTimeout(() => {
          if (originalText) originalText.textContent = originalLabel;
          button.disabled = false;
        }, 1800);
      }
    }
  }

  customElements.define('soar-collection-main', SoarCollectionMain);
})();
